import type { ApiBody, ApiResponse } from '../../api/contract';
import { formatWalletMoney } from '../wallets/types';

export type WithdrawalSummary = ApiResponse<'/api/v1/affiliate-withdrawals', 'get'>['data'][number];
export type WithdrawalDetail = ApiResponse<'/api/v1/affiliate-withdrawals/{id}', 'get'>['data'];
export type WithdrawalEvent = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/events',
  'get'
>['data'][number];
export type DeliveryAttempt = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/delivery-attempts',
  'get'
>['data'][number];
export type WithdrawalStatus = WithdrawalSummary['status'];
export type WithdrawalDeliveryResult = NonNullable<
  WithdrawalSummary['latestDelivery']
>['normalizedResult'];
export type WithdrawalRetryInput = ApiBody<'/api/v1/affiliate-withdrawals/{id}/retry', 'post'>;
export type WithdrawalResolutionInput = ApiBody<
  '/api/v1/affiliate-withdrawals/{id}/resolutions/preview',
  'post'
>;
export type CreateWithdrawalResolutionInput = ApiBody<
  '/api/v1/affiliate-withdrawals/{id}/resolutions',
  'post'
>;
export type WithdrawalResolutionPreview = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/resolutions/preview',
  'post'
>['data'];
export type WithdrawalApprovalSummary = ApiResponse<
  '/api/v1/affiliate-withdrawals/{id}/resolutions',
  'post'
>['data'];

export interface WithdrawalListFilters {
  readonly partnerId: string;
  readonly agentId: string;
  readonly walletId: string;
  readonly memberId: string;
  readonly username: string;
  readonly partnerUserId: string;
  readonly partnerWithdrawalId: string;
  readonly transferId: string;
  readonly partnerCreditId: string;
  readonly currency: string;
  readonly status: WithdrawalStatus | '';
  readonly failureCode: string;
  readonly deliveryResult: NonNullable<WithdrawalDeliveryResult> | '';
  readonly minimumAmount: string;
  readonly maximumAmount: string;
  readonly createdFrom: string;
  readonly createdTo: string;
  readonly completedFrom: string;
  readonly completedTo: string;
}

export interface WithdrawalListQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly sort: 'createdAt' | 'updatedAt';
  readonly filters: WithdrawalListFilters;
}

export interface WithdrawalCursorQuery {
  readonly cursor: string;
  readonly pageSize: number;
}

export function formatWithdrawalMoney(
  withdrawal: Pick<WithdrawalSummary, 'amount' | 'currencyCode' | 'currencyMinorUnit'>,
) {
  return formatWalletMoney(
    withdrawal.amount,
    withdrawal.currencyCode,
    withdrawal.currencyMinorUnit,
  );
}

/** Masks operational identifiers even when the API already returns a safe summary. */
export function maskWithdrawalIdentifier(value: string | null): string {
  if (value === null || value.length === 0) return '—';
  if (value.length <= 8) return '••••';
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}

/** Formats the server-derived reservation age; never derives it from the workstation clock. */
export function formatReservedAge(seconds: string | null): string {
  if (seconds === null) return '—';
  if (!/^(?:0|[1-9][0-9]*)$/u.test(seconds)) return '—';
  let remaining = BigInt(seconds);
  const day = remaining / 86_400n;
  remaining %= 86_400n;
  const hour = remaining / 3_600n;
  remaining %= 3_600n;
  const minute = remaining / 60n;
  const parts = [];
  if (day > 0n) parts.push(`${day}d`);
  if (hour > 0n || day > 0n) parts.push(`${hour}h`);
  parts.push(`${minute}m`);
  return parts.join(' ');
}

export function safeMaskedDiagnostic(value: string | null): string {
  if (value === null || value.trim().length === 0) return '—';
  if (/authorization|bearer\s|token|credential|secret|raw[ _-]?payload/iu.test(value))
    return '[MASKED]';
  return value.slice(0, 240);
}

export function safeEndpointOrigin(value: string): string {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') return '[MASKED]';
    return `${url.protocol}//${url.host}`;
  } catch {
    return '[MASKED]';
  }
}
